/**
 * Alert Component - Themed inline alert/warning messages
 * Supports warning, error, info, and success variants
 * Supports dismissal with optional "don't show again" checkbox
 */

import { cva, type VariantProps } from 'class-variance-authority';
import { X } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

const SERVICE_TAG = '[ALERT]';

const alertVariants = cva(
  'p-3 rounded-md border text-sm backdrop-blur-sm',
  {
    variants: {
      variant: {
        warning: 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400',
        error: 'bg-red-500/10 border-red-500/30 text-red-700 dark:text-red-400',
        info: 'bg-blue-500/10 border-blue-500/30 text-blue-700 dark:text-blue-400',
        success: 'bg-green-500/10 border-green-500/30 text-green-700 dark:text-green-400'
      }
    },
    defaultVariants: {
      variant: 'warning'
    }
  }
);

export interface AlertProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertVariants> {
  /** Alert title (optional) */
  title?: string;
  /** Enable dismiss button (optional) */
  dismissible?: boolean;
  /** Storage key for "don't show again" preference (required if dismissible) */
  storageKey?: string;
  /** Callback when alert is dismissed (optional) */
  onDismiss?: () => void;
}

/**
 * Alert component for inline notifications and warnings.
 * Styled with semi-transparent backgrounds and subtle borders.
 *
 * @example
 * <Alert variant="warning" title="Edit Mode">
 *   Changes will require re-training your dice model (2-4 hours).
 * </Alert>
 *
 * @example
 * <Alert variant="error">
 *   Failed to connect to server
 * </Alert>
 *
 * @example
 * <Alert
 *   variant="warning"
 *   title="Edit Mode"
 *   dismissible
 *   storageKey="edit-mode-warning-dismissed"
 * >
 *   Changes will require re-training your dice model (2-4 hours).
 * </Alert>
 */
function Alert({
  variant,
  title,
  children,
  className,
  dismissible = false,
  storageKey,
  onDismiss,
  ...props
}: AlertProps) {
  const [isDismissed, setIsDismissed] = React.useState(false);
  const [dontShowAgain, setDontShowAgain] = React.useState(false);

  // Check localStorage on mount if storageKey provided
  React.useEffect(() => {
    if (storageKey && dismissible) {
      try {
        const dismissed = localStorage.getItem(storageKey);
        if (dismissed === 'true') {
          setIsDismissed(true);
        }
      } catch (error) {
        console.warn(`${SERVICE_TAG} localStorage unavailable`, {
          storageKey,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
  }, [storageKey, dismissible]);

  // Development warning for missing storageKey when dismissible
  React.useEffect(() => {
    if (dismissible && !storageKey && process.env.NODE_ENV === 'development') {
      console.warn(
        `${SERVICE_TAG} dismissible=true but no storageKey provided. "Don't show again" will not persist.`
      );
    }
  }, [dismissible, storageKey]);

  const handleDismiss = React.useCallback(() => {
    if (dontShowAgain && storageKey) {
      try {
        localStorage.setItem(storageKey, 'true');
      } catch (error) {
        console.warn(`${SERVICE_TAG} Failed to save dismissal preference`, {
          storageKey,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }
    setIsDismissed(true);
    onDismiss?.();
  }, [dontShowAgain, storageKey, onDismiss]);

  if (isDismissed) {
    return null;
  }

  return (
    <div
      className={cn(alertVariants({ variant }), 'relative', className)}
      role="alert"
      {...props}
    >
      {dismissible && (
        <button
          type="button"
          onClick={handleDismiss}
          className="absolute top-2 right-2 p-1 rounded-sm opacity-70 hover:opacity-100 transition-opacity"
          aria-label="Dismiss alert"
        >
          <X className="h-4 w-4" />
        </button>
      )}

      <div className={cn(dismissible && 'pr-8')}>
        {title && <strong className="block mb-1">{title}</strong>}
        <div>{children}</div>
      </div>

      {dismissible && storageKey && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="checkbox"
            id={`${storageKey}-checkbox`}
            checked={dontShowAgain}
            onChange={(e) => setDontShowAgain(e.target.checked)}
            className="h-3 w-3 rounded border-amber-500/30 accent-amber-500 cursor-pointer"
          />
          <label
            htmlFor={`${storageKey}-checkbox`}
            className="text-xs opacity-80 cursor-pointer"
          >
            Don't show this again
          </label>
        </div>
      )}
    </div>
  );
}

export { Alert, alertVariants };
